import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { folderTypes } from "@redotech/redo-model/views/folders";
import { ReplenishmentRequestView } from "@redotech/redo-model/views/views";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CreateOmsViewFolderModal } from "../../common/create-oms-view-folder-modal";
import { EditOmsViewFolderModal } from "../../common/edit-oms-view-folder-modal";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export interface ReplenishmentRequestViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: ReplenishmentRequestView[],
    updatedFolders: folderTypes["replenishment-requests"][],
  ) => void;
  hasInitallyLoaded: boolean;
  views: ReplenishmentRequestView[];
  folders: folderTypes["replenishment-requests"][];
}

export const ReplenishmentRequestViewsContext = createContext<
  ReplenishmentRequestViewsData | undefined
>(undefined);

export type ViewCounts = Record<string, number>;
export type FolderCounts = Record<string, number>;

export const ReplenishmentRequestViewCountsContext = createContext<
  { viewCounts: ViewCounts; folderCounts: FolderCounts } | undefined
>(undefined);

export const ReloadReplenishmentRequestViewsContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

export const ReplenishmentRequestViewsProvider = memo(
  function ReplenishmentRequestViewsProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const client = useContext(RedoOutboundLabelsRpcClientContext);

    const [views, setViews] = useState<ReplenishmentRequestView[]>([]);
    const [folders, setFolders] = useState<
      folderTypes["replenishment-requests"][]
    >([]);
    const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
    const [folderCounts, setFolderCounts] = useState<Record<string, number>>(
      {},
    );
    const [hasInitallyLoaded, setHasInitallyLoaded] = useState(false);

    const [_, loadViews] = useTriggerLoad(async (signal) => {
      if (!client) {
        return;
      }

      const { views, folders } =
        await client.getReplenishmentRequestViewsAndFolders(null, { signal });
      setHasInitallyLoaded(true);
      setViews(views);
      setFolders(folders);
    });

    const reloadViews = useHandler(async () => {
      loadViews();
    });

    useEffect(() => {
      void reloadViews();
    }, [client, reloadViews]);

    useLoad(
      async (signal) => {
        if (!client || !hasInitallyLoaded) {
          return;
        }
        const result = await client.getReplenishmentRequestViewCounts(null, {
          signal,
        });
        setViewCounts(result.viewCounts);
        setFolderCounts(result.folderCounts);
      },
      [client, hasInitallyLoaded, views],
    );

    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showEditFolderModal, setShowEditFolderModal] = useState(false);

    const [selectedFolder, setSelectedFolder] = useState<
      folderTypes["replenishment-requests"] | null
    >(null);

    const handleCreateFolder = async (name: string) => {
      if (!client) {
        return;
      }
      await client.createReplenishmentRequestViewFolder({ name });
      void reloadViews();
    };

    const handleEditFolder = async (folderId: string, folderName: string) => {
      if (!client) {
        return;
      }
      await client.updateReplenishmentRequestViewFolder({
        folderId,
        name: folderName,
      });
      void reloadViews();
    };

    const handleDeleteFolder = async (folderId: string) => {
      if (!client) {
        return;
      }
      await client.deleteReplenishmentRequestViewFolder({ folderId });
      void reloadViews();
    };

    const updateOrdering = async (
      updatedViews: ReplenishmentRequestView[],
      updatedFolders: folderTypes["replenishment-requests"][],
    ) => {
      if (!client) {
        return;
      }
      setViews(updatedViews);
      setFolders(updatedFolders);

      try {
        await client.saveReplenishmentRequestViewsAndFolders({
          views: updatedViews,
          folders: updatedFolders,
        });
      } catch (error) {
        console.error(
          "Error saving replenishment request view and folder ordering",
        );
        if (error instanceof Error) {
          console.error(error.message);
        } else {
          console.error(error);
        }
      }
    };

    const openCreateFolderModal = useHandler(() => {
      setShowCreateFolderModal(true);
    });

    const openEditFolderModal = useHandler((folderId: string) => {
      const folder = folders?.find((folder) => folder._id === folderId);
      if (folder) {
        setShowEditFolderModal(true);
        setSelectedFolder(folder);
      }
    });

    const reservedFolderNames = useMemo(() => {
      return new Set(folders.map((folder) => folder.name));
    }, [folders]);

    return (
      <ReplenishmentRequestViewsContext.Provider
        value={{
          openCreateFolderModal,
          openEditFolderModal,
          handleDeleteFolder,
          updateOrdering,
          views,
          folders,
          hasInitallyLoaded,
        }}
      >
        <ReloadReplenishmentRequestViewsContext.Provider value={reloadViews}>
          <ReplenishmentRequestViewCountsContext.Provider
            value={{ viewCounts, folderCounts }}
          >
            {children}
          </ReplenishmentRequestViewCountsContext.Provider>
          <CreateOmsViewFolderModal
            isOpen={showCreateFolderModal}
            onClose={() => setShowCreateFolderModal(false)}
            onSubmit={handleCreateFolder}
            reservedFolderNames={reservedFolderNames}
          />
          {selectedFolder && (
            <EditOmsViewFolderModal
              deleteFolder={handleDeleteFolder}
              isOpen={showEditFolderModal}
              onClose={() => setShowEditFolderModal(false)}
              onSubmit={handleEditFolder}
              reservedFolderNames={reservedFolderNames}
              selectedFolder={selectedFolder}
            />
          )}
        </ReloadReplenishmentRequestViewsContext.Provider>
      </ReplenishmentRequestViewsContext.Provider>
    );
  },
);
